import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import type { AssignableUserView } from '../../api/generated/models/AssignableUserView';
import type { TaskItemView } from '../../api/generated/models/TaskItemView';
import { TaskCreateRequest } from '../../api/generated/models/TaskCreateRequest';
import { createTask, deleteTask, patchTask } from '../../store/slices/taskSlice';
import type { AppDispatch } from '../../store/store';
import { TASK_MODAL_STATUSES, toPatchStatus } from '../../utils/taskHelpers';
import TaskAssigneesField from './TaskAssigneesField';

interface TaskFormModalProps {
  isOpen: boolean;
  task?: TaskItemView | null;
  assignableUsers: AssignableUserView[];
  canAssign?: boolean;
  onClose: () => void;
}

const TaskFormModal: React.FC<TaskFormModalProps> = ({
  isOpen,
  task,
  assignableUsers,
  canAssign = true,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const isEdit = Boolean(task?.id);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<string>(TASK_MODAL_STATUSES[0]);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Seed the form when it opens (edit uses the task's values, create resets to defaults).
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setTitle(task?.title ?? '');
    setDescription(task?.description ?? '');
    setStatus(task?.status ?? TASK_MODAL_STATUSES[0]);
    const ids = (task?.assignees ?? [])
      .map((row) => row.id)
      .filter((id): id is string => Boolean(id));
    setSelectedAssigneeIds(ids);
    setSubmitting(false);
  }, [isOpen, task]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const trimmedTitle = title.trim();
  const canSave = trimmedTitle.length > 0 && !submitting;

  const handleSave = async () => {
    if (!canSave) {
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit && task?.id) {
        await dispatch(patchTask({
          taskId: task.id,
          title: trimmedTitle,
          description: description.trim() || null,
          status: toPatchStatus(status),
          assigneeIds: canAssign ? selectedAssigneeIds : undefined,
        })).unwrap();
      } else {
        const created = await dispatch(createTask({
          title: trimmedTitle,
          description: description.trim() || null,
          status: status as TaskCreateRequest.status,
        })).unwrap();
        if (canAssign && created?.id && selectedAssigneeIds.length > 0) {
          await dispatch(patchTask({
            taskId: created.id,
            assigneeIds: selectedAssigneeIds,
          })).unwrap();
        }
      }
      onClose();
    } catch {
      // Keep modal open when the request fails; error is surfaced in taskSlice.
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!task?.id || submitting) {
      return;
    }
    if (!window.confirm(t('tasks_delete_confirm'))) {
      return;
    }

    setSubmitting(true);
    try {
      await dispatch(deleteTask(task.id)).unwrap();
      onClose();
    } catch {
      // Keep modal open when delete fails; error is surfaced in taskSlice.
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay tasks-form-overlay" onClick={handleOverlayClick}>
      <div className="tasks-form-modal" onClick={(event) => event.stopPropagation()}>
        <div className="tasks-form-status-row">
          <label className="tasks-form-status-label" htmlFor="task-form-status">
            {t('tasks_modal_status_label')}
          </label>
          <div className="tasks-detail-status-select-wrap">
            <select
              id="task-form-status"
              className="tasks-detail-status-select"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              disabled={submitting}
            >
              {TASK_MODAL_STATUSES.map((option) => (
                <option key={option} value={option}>
                  {t(`tasks_status_${option}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="tasks-form-field">
          <label className="tasks-form-label" htmlFor="task-form-title">
            {t('tasks_form_title_label')}
          </label>
          <input
            id="task-form-title"
            type="text"
            className="tasks-form-input"
            maxLength={255}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={submitting}
          />
        </div>

        <div className="tasks-form-field">
          <label className="tasks-form-label" htmlFor="task-form-description">
            {t('tasks_form_description_label')}
          </label>
          <textarea
            id="task-form-description"
            className="tasks-form-textarea"
            rows={4}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={submitting}
          />
        </div>

        {canAssign && (
          <TaskAssigneesField
            members={assignableUsers}
            selectedIds={selectedAssigneeIds}
            onChange={setSelectedAssigneeIds}
            disabled={submitting}
          />
        )}

        <div className="tasks-form-actions">
          {isEdit && (
            <button
              type="button"
              className="btn btn-danger tasks-form-delete-btn"
              onClick={handleDelete}
              disabled={submitting}
            >
              {t('tasks_delete')}
            </button>
          )}
          <div className="tasks-form-actions-right">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
              {t('cancelText')}
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={!canSave}>
              {t('tasks_form_save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskFormModal;
